import React from 'react';
import { Form, Upload } from '@ok/okd';

import { toLocale } from '@common/i18n';

import { ACCEPT_ATTR } from '../consts';
import { recruitImageUrl } from '../utils';

import UploadErrorMessages from './UploadErrorMessages';

// A filled upload slot: file icon + name + delete button.
const FilledFile = ({ name, onDelete }) => (
  <div className="recruit-submit__file">
    <div className="recruit-submit__file-icon">
      <img src={recruitImageUrl('submit-file.svg')} />
    </div>
    <div className="recruit-submit__file-name">{name}</div>
    <div className="recruit-submit__file-delete" onClick={onDelete}>
      <img src={recruitImageUrl('submit-cancel.svg')} />
    </div>
  </div>
);

// An empty upload dropzone.
const UploadDropzone = ({ className, onChange }) => (
  <Upload
    className={className}
    onUploadInputRef={(input) => {
      if (input) input.setAttribute('accept', ACCEPT_ATTR);
    }}
    onChange={onChange}
    label={
      <div className="recruit-submit__upload-label">
        <div>{toLocale('recruit_upload_attach')}</div>
        <div className="recruit-submit__upload-hint">
          {toLocale('recruit_upload_attach_hint')}
        </div>
      </div>
    }
    icon={
      <img
        className="recruit-submit__upload-icon"
        src={recruitImageUrl('submit-add.svg')}
      />
    }
  />
);

const ResumeUploadField = ({ app }) => {
  const {
    formData,
    setFormDataByKey,
    setFormDataJobResumes,
    setIsCvUntouched,
    isTotalSizeValid,
    curriculumVitaeErrors,
    jobResumesErrors,
  } = app;

  // Narrow the dropzone when only one empty optional slot remains
  const onlyOneEmptySlot =
    formData?.jobResumes?.filter((slot) => !(slot instanceof File))?.length ===
    1;

  return (
    <React.Fragment>
      <Form.Item label={toLocale('recruit_form_resume')} requireIcon>
        {formData.curriculumVitae !== null && (
          <FilledFile
            name={formData.curriculumVitae.name}
            onDelete={() => {
              setIsCvUntouched(true);
              setFormDataByKey('curriculumVitae', null);
            }}
          />
        )}
        {formData.curriculumVitae === null && (
          <UploadDropzone
            className="recruit-submit__upload"
            onChange={(props) => {
              setIsCvUntouched(false);
              setFormDataByKey('curriculumVitae', props?.list?.[0]);
            }}
          />
        )}
        {curriculumVitaeErrors.length > 0 && (
          <UploadErrorMessages messages={curriculumVitaeErrors} />
        )}
      </Form.Item>
      <Form.Item label={toLocale('recruit_form_cv')}>
        <div className="recruit-submit__upload-slots">
          {/* Filled slots first, then empty dropzones (flex-wrap layout) */}
          {[0, 1].map(
            (index) =>
              formData.jobResumes[index] !== null && (
                <React.Fragment key={`resume-${index}`}>
                  <FilledFile
                    name={formData.jobResumes[index].name}
                    onDelete={() => setFormDataJobResumes(index, null)}
                  />
                  {jobResumesErrors[index].length > 0 && (
                    <UploadErrorMessages messages={jobResumesErrors[index]} />
                  )}
                </React.Fragment>
              )
          )}
          {[0, 1].map(
            (index) =>
              formData.jobResumes[index] === null && (
                <UploadDropzone
                  key={`upload-${index}`}
                  className={
                    onlyOneEmptySlot
                      ? 'recruit-submit__upload recruit-submit__upload--single'
                      : 'recruit-submit__upload'
                  }
                  onChange={(props) =>
                    setFormDataJobResumes(index, props?.list?.[0])
                  }
                />
              )
          )}
          {isTotalSizeValid === false && (
            <UploadErrorMessages
              messages={[toLocale('recruit_upload_total_size_invalid')]}
            />
          )}
        </div>
      </Form.Item>
    </React.Fragment>
  );
};

export default ResumeUploadField;
